import React, { createContext, useContext, useState, useEffect } from 'react';
import AuthService from '../auth/AuthService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await AuthService.checkSession();
      if (response.authenticated) {
        const userData = await AuthService.getCurrentUser();
        setUser(userData.user);
        setFamilies(userData.families || []);
        setAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, remember) => {
    const response = await AuthService.login(email, password, remember);
    setUser(response.user);
    setFamilies(response.families || []);
    setAuthenticated(true);
    return response;
  };

  const signup = async (userData) => {
    const response = await AuthService.signup(userData);
    setUser(response.user);
    setAuthenticated(true);
    return response;
  };

  const logout = async () => {
    await AuthService.logout();
    setUser(null);
    setFamilies([]);
    setAuthenticated(false);
  };

  const refreshUser = async () => {
    try {
      const userData = await AuthService.getCurrentUser();
      setUser(userData.user);
      setFamilies(userData.families || []);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    families,
    authenticated,
    loading,
    login,
    signup,
    logout,
    refreshUser,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};